import { Spacer } from '@chakra-ui/layout'
import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import _ from 'lodash'
import { useEffect, useRef, useState } from 'react'
import * as API from '../../api'
import { graphicalCharterLink } from '../../i18n'
import { useUserAwareTranslation } from '../../i18n/hooks'
import { ButtonPrimary, Checkbox, Input } from '../Base'
import HtmlContainer from '../HtmlContainer'
import type { UploadedFile } from './common'
function isCountry(country: string | undefined, countryCode: string, contains: string): boolean {
  if (_.isEmpty(country) || _.isUndefined(country) || _.isNull(country)) {
    return false
  }
  const countryValue = _.lowerCase(country)
  return countryValue === countryCode || countryValue.indexOf(contains) !== -1
}

interface UserFormData {
  uploaderName?: string
  uploaderPosition?: string
}

interface UserFormProps {
  data?: UserFormData
  uploadedFiles: UploadedFile[]
  user: API.APIUser
  onClose: () => void
  setStep: (step: number) => void
  setIsCompleted: (value: boolean) => void
  setError: (value: boolean) => void
}

const UserForm = ({
  data,
  user,
  onClose,
  setStep,
  setIsCompleted,
  uploadedFiles,
  setError,
}: UserFormProps) => {
  const {
    isOpen: isOpenCGU,
    onOpen: openCGU,
    onClose: closeCGU,
  }: { isOpen: boolean; onOpen: () => void; onClose: () => void } = useDisclosure()

  const { t } = useUserAwareTranslation(user)
  const [saving, setSaving] = useState(false)
  const [isValidateDisabled, setIsValidateDisabled] = useState(false)
  const [country, setCountry] = useState(_.get(user, 'country'))
  const [uploaderName, setUploaderName] = useState('')
  const [uploaderPosition, setUploaderPosition] = useState('')
  const [uploaderEmail, setUploaderEmail] = useState(_.get(user, 'email', ''))
  const [CGUValidation, setCGUValidation] = useState(false)
  const [rightDisclaimer, setRightDisclaimer] = useState(false)
  const [CGURead, setCGURead] = useState(false)
  const [sharePrivateData, setSharePrivateData] = useState(false)

  const isKorea = (country: string | undefined) => isCountry(country, 'ko', 'korea')
  const isMalaysia = (country: string | undefined) => isCountry(country, 'ma', 'malaysia')
  const isSingapore = (country: string | undefined) => isCountry(country, 'sg', 'singapore')
  const isVietnam = (country: string | undefined) => isCountry(country, 'vi', 'viet nam')

  const isSharePrivateData = (country: string | undefined) =>
    isKorea(country) || isMalaysia(country) || isSingapore(country) || isVietnam(country)

  useEffect(() => {
    setIsValidateDisabled(
      _.isEmpty(uploaderName) ||
        _.isEmpty(uploaderPosition) ||
        _.isEmpty(uploaderEmail) ||
        !/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
          uploaderEmail
        ) ||
        !CGUValidation ||
        (isKorea(country) && !CGURead) ||
        !rightDisclaimer ||
        (isSharePrivateData(country) && !sharePrivateData)
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    uploaderName,
    uploaderPosition,
    uploaderEmail,
    CGUValidation,
    rightDisclaimer,
    CGURead,
    sharePrivateData,
  ])

  const reset = () => {
    setStep(0)
    setUploaderName('')
    setUploaderPosition('')
    setUploaderEmail(_.get(user, 'email'))
    setCGUValidation(false)
    setRightDisclaimer(false)
    setCGURead(false)
    setSharePrivateData(false)
  }

  const close = () => {
    reset()
    onClose()
  }

  useEffect(() => {
    if (data) {
      setUploaderName(_.get(data, 'uploaderName') || '')
      setUploaderPosition(_.get(data, 'uploaderPosition') || '')
    }
    if (user) {
      setUploaderEmail(_.get(user, 'email', '') || '')
      setCountry(_.get(user, 'country'))
    }
  }, [data, user])

  const save = async () => {
    console.log('save')
    setSaving(true)
    const request = {
      files: uploadedFiles.map((uploadeFile) => _.get(uploadeFile, 'id')),
      userId: _.get(user, 'inspecId'),
      language: _.get(user, 'spokenLanguages[0]', 'en'),
      country,
      uploaderName,
      uploaderPosition,
      uploaderEmail,
      CGUValidationDate: CGUValidation ? new Date() : null,
      rightDisclaimerDate: rightDisclaimer ? new Date() : null,
      CGUReadDate: CGURead ? new Date() : null,
      city: _.get(user, 'city'),
      poiName: _.get(user, 'poiName'),
    }
    console.log('save request', request)
    const [response, err] = await API.validate(user.token, request)
    console.log('save response', response)
    console.log('save err', err)
    if (err) {
      // console.log('[UserForm.save]', err)
      setError(true)
    }
    if (response) {
      //close()
      setError(false)
    }
    setIsCompleted(true)
    reset()
    setSaving(false)
  }

  return (
    <>
      <Flex direction={['column', 'row']}>
        <Grid templateColumns={['repeat(1, 1fr)', 'repeat(3, 1fr)']} gap={6} w="100%">
          <FormControl isRequired>
            <FormLabel>{t('upload-form-uploader-name')}</FormLabel>
            <Input
              placeholder={t('upload-form-uploader-name')}
              value={uploaderName}
              onChange={(e) => setUploaderName(e.target.value)}
            />
          </FormControl>

          <FormControl isRequired>
            <FormLabel>{t('upload-form-uploader-position')}</FormLabel>
            <Input
              placeholder={t('upload-form-uploader-position')}
              value={uploaderPosition}
              onChange={(e) => setUploaderPosition(e.target.value)}
            />
          </FormControl>

          <FormControl isRequired>
            <FormLabel>{t('upload-form-email')}</FormLabel>
            <Input
              type="email"
              placeholder={t('upload-form-email')}
              value={uploaderEmail}
              onChange={(e) => setUploaderEmail(e.target.value)}
            />
          </FormControl>
          <Spacer />
        </Grid>
      </Flex>
      <VStack spacing={2} direction="row" mt={4} alignItems="flex-start">
        {isSharePrivateData(country) && (
          <Checkbox
            isRequired={true}
            borderColor="blackAlpha.500"
            isChecked={sharePrivateData}
            colorScheme="red"
            onChange={(e) => setSharePrivateData(e.target.checked)}>
            {t('upload-form-share-private-data')}{' '}
            <Text as="span" color="red">
              {'*'}
            </Text>
          </Checkbox>
        )}
        <Checkbox
          isRequired
          isChecked={CGUValidation}
          borderColor="blackAlpha.500"
          colorScheme="red"
          onChange={() => {
            openCGU()
            //setCGUValidation(e.target.checked)
          }}>
          {t('upload-form-cgu-validation')}
          <Text as="span" color="red">
            {'*'}
          </Text>
        </Checkbox>
        {isKorea(country) && (
          <Checkbox
            isRequired={true}
            borderColor="blackAlpha.500"
            isChecked={CGURead}
            colorScheme="red"
            onChange={(e) => setCGURead(e.target.checked)}>
            {t('upload-form-cgu-read')}{' '}
            <Text as="span" color="red">
              {'*'}
            </Text>
          </Checkbox>
        )}
        <Checkbox
          borderColor="blackAlpha.500"
          isChecked={rightDisclaimer}
          colorScheme="red"
          onChange={(e) => setRightDisclaimer(e.target.checked)}>
          {t('upload-form-right-disclaimer')}{' '}
          <Text as="span" color="red">
            {'*'}
          </Text>
        </Checkbox>
        <Flex pt="20px">
          <HtmlContainer html={t('mandatory-fields')} />
        </Flex>
      </VStack>
      <Flex direction={['column', 'row']} mb={4}>
        <Spacer />
        <ButtonPrimary
          mt={4}
          w={['100%', '190px']}
          mr={[0, 3]}
          onClick={close}
          isLoading={saving}
          reverse={true}>
          {t('cancel-all')}
        </ButtonPrimary>
        <ButtonPrimary
          mt={4}
          w={['100%', '190px']}
          reverse={false}
          onClick={save}
          isLoading={saving}
          isDisabled={isValidateDisabled}
          loadingText={t('validate-loading-text')}>
          {t('validate')}
        </ButtonPrimary>
      </Flex>
      <ModalCGU
        user={user}
        isOpen={isOpenCGU}
        onClose={() => {
          setCGUValidation(false)
          closeCGU()
        }}
        save={() => {
          setCGUValidation(true)
          closeCGU()
        }}
      />
    </>
  )
}
interface ModalCGUProps {
  user?: API.APIUser
  isOpen: boolean
  onClose: () => void
  save: () => void
}
const ModalCGU = ({ user, isOpen, onClose, save }: ModalCGUProps) => {
  const btnRef = useRef(null)
  const { t } = useUserAwareTranslation(user)
  const [html, setHtml] = useState('')

  const deleteExtension = (src: string, ext: string): string => {
    return src.substring(0, src.length - ext.length)
  }

  useEffect(() => {
    try {
      import(
        `../../i18n/contents/${deleteExtension(t('terms-of-use-html-content'), '.html')}.html?raw`
      ).then((__html) => {
        let rawHtml: string = __html.default
        if (rawHtml.includes('{{guidelines}}')) {
          graphicalCharterLink(user).then((guidelinesLink) => {
            if (guidelinesLink) {
              rawHtml = rawHtml.replaceAll('{{guidelines}}', guidelinesLink)
              setHtml(rawHtml)
            }
          })
        } else {
          setHtml(rawHtml)
        }
      })
    } catch (error) {
      console.error('[ModalCGU]', error)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Modal
      onClose={onClose}
      finalFocusRef={btnRef}
      isOpen={isOpen}
      scrollBehavior="inside"
      size={'3xl'}>
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalBody>
          <Box overflowY={'scroll'} my="20px" h={'300px'} overflowX={'hidden'}>
            {!_.isEmpty(html) && <HtmlContainer html={html} />}
            <Flex>
              <ButtonPrimary m={4} w={['100%', '190px']} reverse={true} onClick={onClose}>
                {t('refuse')}
              </ButtonPrimary>
              <ButtonPrimary m={4} w={['100%', '190px']} onClick={save}>
                {t('accept')}
              </ButtonPrimary>
            </Flex>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

export default UserForm
